import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

/**
 * Receiving responses (restock, initial stock) return the updated workspace
 * plus `receipt`: the labels that receipt needs, for the "Print X labels"
 * follow-up. The workspace shape is unchanged; `receipt` rides along.
 */
const withReceivingReceipt = (res) => {
  if (!res?.success) return res ?? null;
  if (!res.data || res.receipt === undefined) return res.data;
  return { ...res.data, receipt: res.receipt };
};

export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { acceptJson: true }),
  }),
  tagTypes: ["Products", "Product", "Inventory", "Location", "CatalogProduct", "CatalogInventoryHistory"],

  endpoints: (builder) => ({
    fetchProducts: builder.query({
      query: (params) => ({ url: "products/products", params }),
      transformResponse: (res) => (res?.success ? res.data : res ?? []),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((p) => ({ type: "Product", id: p.id })),
              { type: "Products", id: "LIST" },
            ]
          : [{ type: "Products", id: "LIST" }],
    }),

    fetchProductsByType: builder.query({
      query: (type) => ({
        url: "products/products",
        params: {
          type: typeof type === "string" ? type.toLowerCase() : type,
        },
      }),
      transformResponse: (res) => {
        const list = res?.data?.data ?? res?.data ?? res ?? [];
        return Array.isArray(list)
          ? list.map((p) => ({ ...p, selected: true }))
          : [];
      },
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((p) => ({ type: "Product", id: p.id })),
              { type: "Products", id: "LIST" },
            ]
          : [{ type: "Products", id: "LIST" }],
    }),

    addProduct: builder.mutation({
      query: (formDataOrObject) => {
        const body =
          typeof FormData !== "undefined" &&
          formDataOrObject instanceof FormData
            ? formDataOrObject
            : formDataOrObject;
        return {
          url: "products/products",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Products", id: "LIST" }, "Inventory"],
    }),

    previewProductCatalogPlan: builder.mutation({
      query: (body) => ({
        url: "products/catalog/preview",
        method: "POST",
        body,
      }),
    }),

    createCatalogProductDraft: builder.mutation({
      query: (body) => ({
        url: "products/catalog/drafts",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: [{ type: "CatalogProduct", id: "LIST" }],
    }),

    uploadCatalogMediaAsset: builder.mutation({
      query: (body) => ({
        url: "products/catalog/media/upload",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
    }),

    fetchCatalogProducts: builder.query({
      query: (params) => ({ url: "products/catalog", params }),
      transformResponse: (res) =>
        res?.success
          ? res.data
          : res ?? {
              items: [],
              total: 0,
              page: 1,
              pageSize: 20,
              totalPages: 1,
              summary: {
                byStatus: {},
                bySourceType: {},
                byTransitionState: {},
                totalVariants: 0,
                totalAvailableToSell: 0,
              },
            },
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((product) => ({
                type: "CatalogProduct",
                id: product.id,
              })),
              { type: "CatalogProduct", id: "LIST" },
            ]
          : [{ type: "CatalogProduct", id: "LIST" }],
    }),

    fetchRecentCatalogVariants: builder.query({
      query: (params) => ({ url: "products/catalog/recent-variants", params }),
      transformResponse: (res) =>
        res?.success
          ? res.data
          : res ?? {
              items: [],
              total: 0,
              limit: 40,
              sortBy: "created_desc",
            },
    }),

    resolveCatalogBarcode: builder.query({
      query: (params) => ({ url: "products/catalog/resolve-barcode", params }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (result) =>
        result?.product?.id
          ? [{ type: "CatalogProduct", id: result.product.id }]
          : [],
    }),

    checkCatalogProductCode: builder.query({
      query: ({ title, excludeProductId }) => ({
        url: "products/catalog/code-availability",
        params: { title, ...(excludeProductId ? { excludeProductId } : {}) },
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      keepUnusedDataFor: 0,
    }),

    fetchCatalogProductWorkspace: builder.query({
      query: (productId) => `products/catalog/${productId}/workspace`,
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (_result, _error, productId) => [
        { type: "CatalogProduct", id: productId },
      ],
    }),

    fetchCatalogInventoryHistory: builder.query({
      query: ({ productId, params }) => ({
        url: `products/catalog/${productId}/inventory-history`,
        params,
      }),
      transformResponse: (res) =>
        res?.success
          ? res.data
          : res ?? {
              items: [],
              total: 0,
              limit: 20,
            },
      providesTags: (_result, _error, { productId }) => [
        { type: "CatalogInventoryHistory", id: productId },
      ],
    }),

    updateCatalogProductCore: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/core`,
        method: "PUT",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    saveCatalogProductStructure: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/structure`,
        method: "PUT",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    updateCatalogVariants: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/variants`,
        method: "PUT",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    replaceCatalogProductMedia: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/media`,
        method: "PUT",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    updateCatalogProductInventory: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/inventory`,
        method: "PUT",
        body,
      }),
      transformResponse: withReceivingReceipt,
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    restockCatalogProduct: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/restock`,
        method: "POST",
        body,
      }),
      transformResponse: withReceivingReceipt,
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
        { type: "CatalogInventoryHistory", id: productId },
      ],
    }),

    adjustCatalogProductInventory: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/inventory-adjustments`,
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
        { type: "CatalogInventoryHistory", id: productId },
      ],
    }),

    receiveCatalogBarcodeScan: builder.mutation({
      query: (body) => ({
        url: "products/catalog/receiving/scan",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (result) =>
        result?.product?.id
          ? [
              { type: "CatalogProduct", id: result.product.id },
              { type: "CatalogProduct", id: "LIST" },
              { type: "CatalogInventoryHistory", id: result.product.id },
            ]
          : [{ type: "CatalogProduct", id: "LIST" }],
    }),

    undoCatalogReceivingScan: builder.mutation({
      query: (body) => ({
        url: "products/catalog/receiving/scan/undo",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (result) =>
        result?.product?.id
          ? [
              { type: "CatalogProduct", id: result.product.id },
              { type: "CatalogProduct", id: "LIST" },
              { type: "CatalogInventoryHistory", id: result.product.id },
            ]
          : [{ type: "CatalogProduct", id: "LIST" }],
    }),

    updateCatalogProductPublishing: builder.mutation({
      query: ({ productId, body }) => ({
        url: `products/catalog/${productId}/publishing`,
        method: "PUT",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "CatalogProduct", id: productId },
        { type: "CatalogProduct", id: "LIST" },
      ],
    }),

    fetchCatalogMigrationAudit: builder.query({
      query: () => "products/catalog/migration/audit",
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: [{ type: "CatalogProduct", id: "MIGRATION_AUDIT" }],
    }),

    executeCatalogMigration: builder.mutation({
      query: (body) => ({
        url: "products/catalog/migration/execute",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: [
        { type: "CatalogProduct", id: "LIST" },
        { type: "CatalogProduct", id: "MIGRATION_AUDIT" },
      ],
    }),

    transferProducts: builder.mutation({
      query: ({ ids, location }) => ({
        url: "products/transfer",
        method: "PUT",
        body: { ids, location },
      }),
      invalidatesTags: [
        { type: "Products", id: "LIST" },
        "Inventory",
        "Location",
      ],
    }),

    sellProducts: builder.mutation({
      query: (saleData) => ({
        url: "products/products",
        method: "PUT",
        body: saleData,
      }),
      invalidatesTags: [{ type: "Products", id: "LIST" }, "Inventory"],
    }),

    returnProducts: builder.mutation({
      query: (returnData) => ({
        url: "products/return",
        method: "POST",
        body: returnData,
      }),
      invalidatesTags: [{ type: "Products", id: "LIST" }, "Inventory"],
    }),
  }),
});

export const {
  useFetchProductsQuery,
  useFetchProductsByTypeQuery,
  useAddProductMutation,
  usePreviewProductCatalogPlanMutation,
  useCreateCatalogProductDraftMutation,
  useUploadCatalogMediaAssetMutation,
  useFetchCatalogProductsQuery,
  useFetchRecentCatalogVariantsQuery,
  useLazyResolveCatalogBarcodeQuery,
  useLazyCheckCatalogProductCodeQuery,
  useFetchCatalogProductWorkspaceQuery,
  useLazyFetchCatalogProductWorkspaceQuery,
  useFetchCatalogInventoryHistoryQuery,
  useUpdateCatalogProductCoreMutation,
  useSaveCatalogProductStructureMutation,
  useUpdateCatalogVariantsMutation,
  useReplaceCatalogProductMediaMutation,
  useUpdateCatalogProductInventoryMutation,
  useRestockCatalogProductMutation,
  useAdjustCatalogProductInventoryMutation,
  useReceiveCatalogBarcodeScanMutation,
  useUndoCatalogReceivingScanMutation,
  useUpdateCatalogProductPublishingMutation,
  useFetchCatalogMigrationAuditQuery,
  useExecuteCatalogMigrationMutation,
  useTransferProductsMutation,
  useSellProductsMutation,
  useReturnProductsMutation, // ✅ Export new hook
} = productsApi;
